import { Button } from '../ui/button';
import { Download, FileText, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Professional Report Generator & Export Toolbar for Accounting.
 *
 * Props:
 *   title        – Report main title
 *   subtitle     – Branch name or date range
 *   buildReport  – () => { kpis: [{label, value, unit?, highlight?}], sections: [{title, type: 'table'|'kpis'|'text', headers?, rows?, items?, text?}] }
 *   csvData      – { data: [], columns: [{key, label}] } for CSV export
 *   t            – translation function
 *   lang         – 'ar' | 'en'
 */

const REPORT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Tajawal', 'Segoe UI', sans-serif;
    direction: rtl; text-align: right;
    line-height: 1.7; color: #1a1a2e;
    max-width: 210mm; margin: 0 auto;
    padding: 32px; background: #fff;
  }
  .report-header {
    display: flex; align-items: center; justify-content: space-between;
    border-bottom: 3px solid #d4a574; padding-bottom: 20px; margin-bottom: 28px;
  }
  .report-header .brand { display: flex; align-items: center; gap: 12px; }
  .report-header .logo {
    width: 48px; height: 48px; border-radius: 10px;
    background: linear-gradient(135deg, #d4a574, #b8860b);
    display: flex; align-items: center; justify-content: center;
    color: white; font-weight: 800; font-size: 20px;
  }
  .report-header h1 { font-size: 22px; font-weight: 800; color: #1a1a2e; margin: 0; }
  .report-header .subtitle { font-size: 13px; color: #666; margin-top: 2px; }
  .report-header .meta { text-align: left; font-size: 11px; color: #888; }
  .report-header .meta div { margin-bottom: 2px; }

  .kpi-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px; margin-bottom: 28px;
  }
  .kpi-card {
    border: 1px solid #e8e8e8; border-radius: 10px; padding: 16px; text-align: center;
    background: #fafafa;
  }
  .kpi-card.highlight { border-color: #d4a574; background: #fdf8f3; }
  .kpi-card.negative { border-color: #e74c3c; background: #fef5f5; }
  .kpi-label { font-size: 11px; color: #888; margin-bottom: 4px; }
  .kpi-value { font-size: 22px; font-weight: 800; color: #1a1a2e; }
  .kpi-value.positive { color: #27ae60; }
  .kpi-value.negative { color: #e74c3c; }
  .kpi-unit { font-size: 11px; color: #999; font-weight: 400; }

  .section { margin-bottom: 28px; }
  .section-title {
    font-size: 15px; font-weight: 700; color: #1a1a2e;
    padding-bottom: 8px; margin-bottom: 14px;
    border-bottom: 2px solid #f0e6d8; display: flex; align-items: center; gap: 8px;
  }
  .section-title::before {
    content: ''; width: 4px; height: 18px; border-radius: 2px;
    background: linear-gradient(135deg, #d4a574, #b8860b); display: inline-block;
  }

  table {
    width: 100%; border-collapse: collapse; font-size: 12.5px;
    border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;
  }
  thead th {
    background: linear-gradient(135deg, #2c2c3a, #3d3d50);
    color: white; padding: 10px 14px; font-weight: 600;
    text-align: right; font-size: 12px; white-space: nowrap;
  }
  tbody td { padding: 9px 14px; border-bottom: 1px solid #f0f0f0; }
  tbody tr:nth-child(even) { background: #fafafa; }
  tbody tr:hover { background: #f5f0ea; }
  .td-num { font-variant-numeric: tabular-nums; font-weight: 600; }
  .td-positive { color: #27ae60; }
  .td-negative { color: #e74c3c; }
  .total-row { background: #f0e6d8 !important; font-weight: 700; }
  .total-row td { border-top: 2px solid #d4a574; }

  .text-block { font-size: 13px; color: #444; padding: 14px; background: #fafafa; border-radius: 8px; border-right: 4px solid #d4a574; }

  .comparison-row { display: flex; gap: 16px; margin-bottom: 16px; }
  .comparison-card {
    flex: 1; border: 1px solid #e8e8e8; border-radius: 8px; padding: 12px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .comp-label { font-size: 11px; color: #888; }
  .comp-ref { font-size: 12px; color: #666; margin-top: 2px; }
  .comp-value { font-size: 16px; font-weight: 700; }
  .comp-up { color: #27ae60; }
  .comp-down { color: #e74c3c; }

  .report-footer {
    margin-top: 40px; padding-top: 16px; border-top: 1px solid #e0e0e0;
    display: flex; justify-content: space-between; font-size: 10px; color: #aaa;
  }

  @media print {
    body { padding: 16px; }
    @page { size: A4; margin: 12mm; }
    table { page-break-inside: avoid; }
    .section { page-break-inside: avoid; }
    .report-footer { position: fixed; bottom: 0; left: 0; right: 0; padding: 8px 16px; }
  }
`;

function buildReportHTML({ title, subtitle, kpis, sections, lang }) {
  const now = new Date();
  const dateStr = now.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' });

  let html = `<!DOCTYPE html><html lang="${lang}" dir="rtl"><head>
    <meta charset="UTF-8"><title>${title}</title>
    <style>${REPORT_CSS}</style></head><body>`;

  // Header
  html += `<div class="report-header">
    <div class="brand">
      <div class="logo">Z</div>
      <div><h1>${title}</h1><div class="subtitle">${subtitle || ''}</div></div>
    </div>
    <div class="meta">
      <div>${dateStr}</div><div>${timeStr}</div>
      <div>Zouq AI</div>
    </div>
  </div>`;

  // Top KPIs
  if (kpis?.length) {
    html += `<div class="kpi-grid">`;
    for (const k of kpis) {
      const cls = k.highlight === 'negative' ? 'negative' : k.highlight ? 'highlight' : '';
      const valCls = k.highlight === 'positive' ? 'positive' : k.highlight === 'negative' ? 'negative' : '';
      html += `<div class="kpi-card ${cls}">
        <div class="kpi-label">${k.label}</div>
        <div class="kpi-value ${valCls}">${k.value} <span class="kpi-unit">${k.unit || ''}</span></div>
      </div>`;
    }
    html += `</div>`;
  }

  // Sections
  for (const sec of (sections || [])) {
    html += `<div class="section">`;
    if (sec.title) html += `<div class="section-title">${sec.title}</div>`;

    if (sec.type === 'kpis' && sec.items) {
      html += `<div class="kpi-grid">`;
      for (const k of sec.items) {
        const cls = k.highlight === 'negative' ? 'negative' : k.highlight ? 'highlight' : '';
        const valCls = k.highlight === 'positive' ? 'positive' : k.highlight === 'negative' ? 'negative' : '';
        html += `<div class="kpi-card ${cls}">
          <div class="kpi-label">${k.label}</div>
          <div class="kpi-value ${valCls}">${k.value} <span class="kpi-unit">${k.unit || ''}</span></div>
        </div>`;
      }
      html += `</div>`;
    }

    if (sec.type === 'table' && sec.headers && sec.rows) {
      html += `<table><thead><tr>`;
      for (const h of sec.headers) html += `<th>${h}</th>`;
      html += `</tr></thead><tbody>`;
      for (const row of sec.rows) {
        const rowCls = row._total ? 'total-row' : '';
        html += `<tr class="${rowCls}">`;
        for (let i = 0; i < row.cells.length; i++) {
          const cell = row.cells[i];
          const tdCls = cell.type === 'num' ? 'td-num' : '';
          const colorCls = cell.color === 'positive' ? 'td-positive' : cell.color === 'negative' ? 'td-negative' : '';
          html += `<td class="${tdCls} ${colorCls}">${cell.value}</td>`;
        }
        html += `</tr>`;
      }
      html += `</tbody></table>`;
    }

    if (sec.type === 'comparison' && sec.items) {
      html += `<div class="comparison-row">`;
      for (const c of sec.items) {
        const cls = c.change > 0 ? 'comp-up' : c.change < 0 ? 'comp-down' : '';
        const arrow = c.change > 0 ? '▲' : c.change < 0 ? '▼' : '';
        html += `<div class="comparison-card">
          <div><div class="comp-label">${c.label}</div><div class="comp-ref">${c.reference}</div></div>
          <div class="comp-value ${cls}">${arrow} ${c.change > 0 ? '+' : ''}${c.change}%</div>
        </div>`;
      }
      html += `</div>`;
    }

    if (sec.type === 'text') {
      html += `<div class="text-block">${sec.text}</div>`;
    }

    html += `</div>`;
  }

  // Footer
  const footerLeft = lang === 'ar' ? 'تم الإنشاء بواسطة Zouq AI' : 'Generated by Zouq AI';
  html += `<div class="report-footer">
    <span>${footerLeft}</span>
    <span>${dateStr} - ${timeStr}</span>
  </div>`;

  html += `</body></html>`;
  return html;
}

export default function ExportToolbar({ title, subtitle, buildReport, printId, csvData, t, lang }) {

  const openReport = (autoPrint) => {
    let html;

    if (buildReport) {
      const reportData = buildReport();
      if (reportData) {
        html = buildReportHTML({ title, subtitle, lang, ...reportData });
      }
    }

    // Fallback: if no buildReport or it returned null, use printId DOM capture
    if (!html && printId) {
      const el = document.getElementById(printId);
      if (el) {
        html = buildReportHTML({
          title, subtitle, lang,
          kpis: [],
          sections: [{ type: 'text', title: '', text: el.innerText }]
        });
      }
    }

    if (!html) {
      toast.error(t('export.noData'));
      return;
    }

    const w = window.open('', '_blank');
    if (!w) {
      toast.error(t('export.popupBlocked'));
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    if (autoPrint) {
      setTimeout(() => { w.print(); }, 600);
    }
  };

  const handleCSV = () => {
    const { data, columns } = csvData || {};
    if (!data?.length || !columns?.length) { toast.error(t('export.noData')); return; }
    const BOM = '\uFEFF';
    const header = columns.map(c => c.label).join(',');
    const rows = data.map(row =>
      columns.map(c => {
        let val = row[c.key] ?? '';
        if (typeof val === 'string' && (val.includes(',') || val.includes('"') || val.includes('\n')))
          val = `"${val.replace(/"/g, '""')}"`;
        return val;
      }).join(',')
    );
    const csv = BOM + header + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('export.csvDone'));
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="export-toolbar">
      <Button variant="outline" size="sm" onClick={() => openReport(true)} className="gap-1.5 text-xs" data-testid="btn-print">
        <Printer className="w-3.5 h-3.5" /> {t('export.print')}
      </Button>
      <Button variant="outline" size="sm" onClick={() => openReport(false)} className="gap-1.5 text-xs" data-testid="btn-pdf">
        <FileText className="w-3.5 h-3.5" /> PDF
      </Button>
      {csvData?.data?.length > 0 && csvData?.columns?.length > 0 && (
        <Button variant="outline" size="sm" onClick={handleCSV} className="gap-1.5 text-xs" data-testid="btn-csv">
          <Download className="w-3.5 h-3.5" /> CSV
        </Button>
      )}
    </div>
  );
}
